import {
  ChecklistQuestion,
  CreateChecklistQuestionInput,
  ROLES_NO_CHECKLIST,
  SubmitChecklist,
  ValidateChecklistResponseParams,
} from '@rapid-recovery-agency-inc/insightt-graphql';
import {
  ChecklistQuestionCategory,
  ChecklistQuestionsFormQuery,
  GroupByChecklistQuestionByRoleAndCategoryQuery,
  RolesType,
} from '@rapid-recovery-agency-inc/insightt-graphql';
import { ChecklistItemsState, ShiftChecklistResponse } from './redux/checklistSlice';
import { getNameFromPhotoKey, SessionRole } from '@rapid-recovery-agency-inc/insightt-shared';

export function formatSnakeCaseString(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function enumToFormattedArray<T extends Record<string, string>>(enumObject: T): string[] {
  return Object.values(enumObject).map(formatSnakeCaseString);
}

type ChecklistQuestionCategoryKey = keyof typeof ChecklistQuestionCategory;
const orderedKeys: ChecklistQuestionCategoryKey[] = ['NEW_HIRE', 'TERMINATE', 'START_SHIFT', 'END_SHIFT'];
export function getOrderedHeaderChecklist() {
  return orderedKeys.map((key) => ChecklistQuestionCategory[key]);
}

export function getRolesTypes() {
  const filteredRoles = Object.values(RolesType).filter((role) => !ROLES_NO_CHECKLIST.includes(role));
  return filteredRoles;
}

export function generateUniqueId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function generateCombinations(
  selectedCategories: ChecklistQuestionCategory[],
  selectedRoles: number[],
  checklistItems: {
    id: string;
    question: string;
    input: number;
    photo: number;
  }[],
  reference: GroupByChecklistQuestionByRoleAndCategoryQuery['groupByChecklistQuestion'],
) {
  const combinations: CreateChecklistQuestionInput[] = [];

  selectedCategories.forEach((category) => {
    selectedRoles.forEach((roleId) => {
      checklistItems.forEach((item, index) => {
        const referenceCount = reference.find(
          (question) => question.category === category && question.roleId === roleId,
        )?._count?._all;
        combinations.push({
          category,
          index: referenceCount || index,
          question: item.question,
          file: 0,
          input: item.input,
          photo: item.photo,
          roleId: roleId,
        });
      });
    });
  });

  return combinations;
}

export const formatDataToSave = (
  selectedRole: RolesType[],
  checklistItems: ChecklistItemsState[],
  roles: SessionRole[],
) => {
  const rolesId = selectedRole
    .map((role) => roles.find((r) => r.type === role)?.id)
    .filter((id) => id !== undefined) as number[];
  const checklistDataFormatted = checklistItems.map((item) => ({
    id: item.id,
    question: item.text,
    input: Number(item.input),
    photo: Number(item.photo),
  }));

  return { rolesId, checklistDataFormatted };
};

export function formatChecklistResponseForSubmit(
  responses: ShiftChecklistResponse[],
  uploadedFiles?: { key: string; url: string }[],
): SubmitChecklist['checklistResponses'] {
  return responses.map(({ question, response, comment, photoFile }) => {
    const uploadedFile = uploadedFiles?.find((file) => getNameFromPhotoKey(file.key) === photoFile?.name);

    return {
      question,
      response,
      comment,
      ...(uploadedFile && { photoUrl: uploadedFile.url, photoKey: uploadedFile.key }),
    };
  });
}

export const assignIndexByPosition = (data: ChecklistQuestionsFormQuery['checklistQuestions']) => {
  return data.map((item, index) => ({ ...item, index }));
};

export const mapChecklistResponsesToValidationParams = ({
  responses,
}: {
  responses: ShiftChecklistResponse[];
}): ValidateChecklistResponseParams[] => {
  return responses.map((response) => ({
    ...response,
    photo: response.photoFile,
  }));
};

interface UpsertResponseParams {
  currentResponses: ShiftChecklistResponse[];
  question: Pick<ChecklistQuestion, 'id' | 'question'>;
  updates: Partial<ShiftChecklistResponse>;
  allQuestions: Pick<ChecklistQuestion, 'id' | 'question' | 'input' | 'photo'>[];
}

/**
 * Immutably updates an existing response in the checklist
 * or inserts a new one if it is being answered for the first time (Upsert).
 * It also maintains the original question order once all questions are answered.
 */
export const upsertChecklistResponse = ({
  currentResponses,
  question,
  updates,
  allQuestions,
}: UpsertResponseParams): ShiftChecklistResponse[] => {
  const exists = currentResponses.some((r) => r.id === question.id);

  let updated = exists
    ? currentResponses.map((r) => (r.id === question.id ? { ...r, ...updates } : r))
    : [
        ...currentResponses,
        { id: question.id, question: question.question, response: updates.response ?? '', ...updates },
      ];

  if (updated.length === allQuestions.length) {
    updated = [...updated].sort(
      (a, b) => allQuestions.findIndex((q) => q.id === a.id) - allQuestions.findIndex((q) => q.id === b.id),
    );
  }

  return updated;
};
